import { isEqual, uniq } from 'lodash';

import {
  AugmentedUpdatePresentationPageInput,
  PresentationPageKind,
  UpdatePresentationCoverPageInput,
  UpdatePresentationCustomPageInput,
  UpdatePresentationDiagramPageInput,
} from '@/types/schema';

import { ClientPresentationV2Page } from '../../types/ClientPresentation.PresentationPageType';
import { PresentationSaveContext } from '../presentationSave.types';

export type PageContentPayload = Omit<
  AugmentedUpdatePresentationPageInput,
  'id' | 'update'
>;

const DIFFABLE_PAGE_CHILDREN = [
  'coverPage',
  'customPage',
  'diagramPage',
] as const;

/** These children are browser-only or persisted outside presentation save. */
const TRANSIENT_PAGE_CHILDREN = [
  'balanceSheetBundleConfiguration',
  'educationalSlide',
] as const;

const PAGE_IDENTITY_FIELDS = ['id', 'type'] as const;

function diffCoverPage(
  previous: NonNullable<ClientPresentationV2Page['coverPage']>,
  next: NonNullable<ClientPresentationV2Page['coverPage']>
): UpdatePresentationCoverPageInput | null {
  const update: UpdatePresentationCoverPageInput = {};

  if (previous.customContent !== next.customContent) {
    if (next.customContent) {
      update.customContent = next.customContent;
    } else {
      update.clearCustomContent = true;
    }
  }
  if (previous.displayTitle !== next.displayTitle) {
    if (next.displayTitle) {
      update.displayTitle = next.displayTitle;
    } else {
      update.clearDisplayTitle = true;
    }
  }
  if (previous.customPrompt !== next.customPrompt) {
    if (next.customPrompt) {
      update.customPrompt = next.customPrompt;
    } else {
      update.clearCustomPrompt = true;
    }
  }

  return Object.keys(update).length > 0 ? update : null;
}

/** Returns false when a changed page child has no writer. */
export function canWritePageContent(
  previous: ClientPresentationV2Page,
  next: ClientPresentationV2Page
): boolean {
  const accountedFor: readonly string[] = [
    ...PAGE_IDENTITY_FIELDS,
    ...DIFFABLE_PAGE_CHILDREN,
    ...TRANSIENT_PAGE_CHILDREN,
  ];
  const keys = uniq([...Object.keys(previous), ...Object.keys(next)]);

  return keys.every((key) => {
    if (accountedFor.includes(key)) return true;
    if (key === 'documentSummaryPage') {
      const before = previous.documentSummaryPage;
      const after = next.documentSummaryPage;
      if (isEqual(before, after)) return true;
      // Only the selected summary can change here. Its text is server-derived;
      // the child identity and the other page settings must stay unchanged.
      return Boolean(
        before?.id &&
          after?.id === before.id &&
          before.documentType === after.documentType &&
          isEqual(before.relevantClientProfiles, after.relevantClientProfiles)
      );
    }
    // The key came from one of these objects, so this cast is safe.
    const field = key as keyof ClientPresentationV2Page;
    return isEqual(previous[field], next[field]);
  });
}

/** `customPrompt` requires its clear flag because omitted fields remain unchanged. */
function diffCustomPage(
  previous: NonNullable<ClientPresentationV2Page['customPage']>,
  next: NonNullable<ClientPresentationV2Page['customPage']>
): UpdatePresentationCustomPageInput | null {
  const update: UpdatePresentationCustomPageInput = {};

  if (previous.pageName !== next.pageName) update.pageName = next.pageName;
  if (previous.pageContent !== next.pageContent) {
    update.pageContent = next.pageContent;
  }
  if (previous.customPrompt !== next.customPrompt) {
    if (next.customPrompt) {
      update.customPrompt = next.customPrompt;
    } else {
      update.clearCustomPrompt = true;
    }
  }

  return Object.keys(update).length > 0 ? update : null;
}

/** Diagram graph fields are replaced as complete arrays. */
function diffDiagramPage(
  previous: NonNullable<ClientPresentationV2Page['diagramPage']>,
  next: NonNullable<ClientPresentationV2Page['diagramPage']>
): UpdatePresentationDiagramPageInput | null {
  const update: UpdatePresentationDiagramPageInput = {};

  if (previous.pageName !== next.pageName) update.pageName = next.pageName;
  if (previous.customPrompt !== next.customPrompt) {
    if (next.customPrompt) {
      update.customPrompt = next.customPrompt;
    } else {
      update.clearCustomPrompt = true;
    }
  }
  if (!isEqual(previous.nodeConfigurations, next.nodeConfigurations)) {
    update.nodeConfigurations = next.nodeConfigurations;
  }
  if (!isEqual(previous.edgeDefinitions, next.edgeDefinitions)) {
    update.edgeDefinitions = next.edgeDefinitions;
  }
  if (!isEqual(previous.phases, next.phases)) {
    if (next.phases) {
      update.phases = next.phases;
    } else {
      update.clearPhases = true;
    }
  }

  return Object.keys(update).length > 0 ? update : null;
}

/**
 * Returns updates for existing page children. Children without server IDs require
 * create payloads and are omitted here.
 */
export function diffPageContent(
  previous: ClientPresentationV2Page,
  next: ClientPresentationV2Page,
  context: PresentationSaveContext
): PageContentPayload | null {
  const payload: PageContentPayload = {};

  const coverPageId = next.coverPage?.id;
  if (previous.coverPage && next.coverPage) {
    if (next.type === PresentationPageKind.WaterfallCoverPage) {
      if (previous.coverPage.customContent === next.coverPage.customContent) {
        return null;
      }
      if (coverPageId) {
        payload.updateWaterfallBundleCoverPage = {
          id: coverPageId,
          update: { content: next.coverPage.customContent },
        };
      } else {
        payload.withWaterfallBundleCoverPage = {
          create: {
            content: next.coverPage.customContent,
            householdID: context.householdId,
          },
        };
      }
      return payload;
    }

    const update = diffCoverPage(previous.coverPage, next.coverPage);
    if (update && coverPageId) {
      payload.updateCoverPage = { id: coverPageId, update };
    } else if (update) {
      // Older cover pages can exist without their type-specific child row.
      // Create that row on the existing page so this edit and later edits have
      // a stable server identity.
      payload.withCoverPage = {
        create: {
          customContent: next.coverPage.customContent,
          customPrompt: next.coverPage.customPrompt ?? undefined,
          displayTitle: next.coverPage.displayTitle,
          householdID: context.householdId,
        },
      };
    }
  }

  const customPageId = next.customPage?.id;
  if (previous.customPage && next.customPage && customPageId) {
    const update = diffCustomPage(previous.customPage, next.customPage);
    if (update) {
      payload.updateCustomPage = { id: customPageId, update };
    }
  }

  const diagramPageId = next.diagramPage?.id;
  if (previous.diagramPage && next.diagramPage && diagramPageId) {
    const update = diffDiagramPage(previous.diagramPage, next.diagramPage);
    if (update) {
      payload.updateDiagramPage = { id: diagramPageId, update };
    }
  }

  const beforeDocumentSummary = previous.documentSummaryPage;
  const afterDocumentSummary = next.documentSummaryPage;
  if (
    beforeDocumentSummary &&
    afterDocumentSummary &&
    beforeDocumentSummary.summary?.id !== afterDocumentSummary.summary?.id
  ) {
    payload.updateDocumentSummaryPage = {
      id: beforeDocumentSummary.id,
      update: afterDocumentSummary.summary?.id
        ? { summaryID: afterDocumentSummary.summary.id }
        : { clearSummary: true },
    };
  }

  return Object.keys(payload).length > 0 ? payload : null;
}
